import { useEffect, useState } from 'react';
import { FRAME_MS } from '../../engine/games/luck';
import { plinkoTable, ROW_MS, ROWS, type PlinkoRisk, type PlinkoState } from '../../engine/games/plinko';
import { money } from '../../format';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

const RISKS: PlinkoRisk[] = ['low', 'medium', 'high'];
const RISK_NAMES: Record<PlinkoRisk, string> = { low: 'Low', medium: 'Medium', high: 'High' };
/** A ★ second chance: the losing ball falls one row per engine frame, then luck. */
const LUCK_AT_MS = (ROWS + 1) * FRAME_MS + 100;

/** Where the ball is after falling `f` rows (fractional) along `path`:
 *  row k's pegs sit at x = ROWS - k + 2j, and the slots at x = 2 × rights. */
function ballAt(path: readonly number[], f: number): { x: number; y: number } {
  const at = (k: number) => {
    if (k < 0) return { x: ROWS, y: -1.2 };
    const rights = path.slice(0, k).reduce((s, d) => s + d, 0);
    return k >= ROWS ? { x: 2 * rights, y: ROWS + 0.55 } : { x: ROWS - k + 2 * rights, y: k };
  };
  const k = Math.floor(f);
  const frac = Math.min(1, Math.max(0, f - k));
  const a = at(k - 1);
  const b = at(Math.min(k, ROWS));
  if (f >= ROWS + 1) return at(ROWS);
  // A little hop off each peg.
  return { x: a.x + (b.x - a.x) * frac, y: a.y + (b.y - a.y) * frac - Math.sin(Math.PI * frac) * 0.35 };
}

/** A path that lands in `slot` (for the ★'s losing ball, known only by its slot). */
function pathTo(slot: number): number[] {
  return Array.from({ length: ROWS }, (_, i) => (Math.floor(((i + 1) * slot) / ROWS) > Math.floor((i * slot) / ROWS) ? 1 : 0));
}

const binColor = (i: number) => ['#3f6fd8', '#2fa4b8', '#3aa55c', '#e08a2a', '#d6332b'][Math.abs(i - ROWS / 2)]!;

export function PlinkoTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.plinko?.state as PlinkoState | undefined;
  const anim = snapshot.anim;
  const luck = secondChanceFrame(snapshot);
  // The losing ball's slot: where the O sits on the board's bottom line.
  const lostSlot = luck ? (() => {
    const line = luck.split('\n').find((l) => /^[_O]+$/.test(l.trim()) && l.includes('_'));
    const i = line?.indexOf('O') ?? -1;
    return i >= 0 ? i / 2 : null;
  })() : null;
  const elapsed = useAnimClock(snapshot, actions, luck ? (anim?.frames.length ?? 0) * FRAME_MS : 0);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, lostSlot !== null ? LUCK_AT_MS : null, () => ({ what: 'A losing ball drops again.', saved: !!state?.lucky }), setBurst);

  // The engine moves the ball a row per tick; redraw smoothly in between.
  const dropping = state?.phase === 'dropping';
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!dropping) return;
    const t = setInterval(() => setNow(Date.now()), 40);
    return () => clearInterval(t);
  }, [dropping]);

  const risk = state && state.phase !== 'idle' ? state.lastRisk : (state?.risk ?? 'medium');
  const table = plinkoTable(state?.phase === 'idle' || !state ? (state?.risk ?? 'medium') : state.lastRisk);

  let ball: { x: number; y: number } | null = null;
  let landed: number | null = null;
  if (anim && lostSlot !== null) ball = ballAt(pathTo(lostSlot), elapsed / FRAME_MS);
  else if (state && dropping) ball = ballAt(state.path, Math.max(0, (now - state.startedAt) / ROW_MS));
  else if (state?.phase === 'done' && state.slot !== null) {
    ball = ballAt(state.path, ROWS + 1);
    landed = state.slot;
  }
  if (anim && lostSlot !== null && elapsed >= (ROWS + 1) * FRAME_MS) landed = lostSlot;

  const setRisk = (r: PlinkoRisk) => {
    if (!state || dropping || anim || r === state.risk) return;
    const steps = (RISKS.indexOf(r) - RISKS.indexOf(state.risk) + RISKS.length) % RISKS.length;
    for (let k = 0; k < steps; k++) actions.tableOption('risk');
  };

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (state?.phase === 'done' && state.slot !== null && !anim) {
    const net = state.returned - state.lastStake;
    result = `Landed ×${table[state.slot]!.toFixed(2)} · ${state.lucky ? '★ ' : ''}${net > 0 ? `+${money(net)}` : net === 0 ? 'even' : `−${money(-net)}`}`;
    tone = net > 0 ? 'win' : net < 0 ? 'loss' : '';
  }

  return (
    <>
      <Stage className="plinko-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <div className="plinko-show">
          <div className="plinko-marquee" aria-hidden="true">
            <span className="bulbs" />
            <span className="plinko-title">Plinko</span>
            <span className="bulbs" />
          </div>
          <svg className="plinko-board" viewBox={`-1 -2 ${ROWS * 2 + 2} ${ROWS + 4.2}`} role="img" aria-label={landed !== null ? `Landed in slot ${landed + 1}` : 'The peg board'}>
            {Array.from({ length: ROWS }, (_, k) => Array.from({ length: k + 1 }, (_, j) => <circle key={`${k}-${j}`} cx={ROWS - k + 2 * j} cy={k} r={0.16} className="peg" />))}
            {table.map((m, i) => (
              <g key={i} className={`bin${landed === i ? ' lit' : ''}`}>
                <rect x={2 * i - 0.92} y={ROWS + 0.1} width={1.84} height={1.9} rx={0.3} fill={binColor(i)} />
                <text x={2 * i} y={ROWS + 1.25} textAnchor="middle">{m >= 10 ? m.toFixed(0) : m.toFixed(1)}</text>
              </g>
            ))}
            {ball && <circle cx={ball.x} cy={ball.y} r={0.42} className="plinko-ball" />}
          </svg>
        </div>
        <div className="plinko-risk" role="radiogroup" aria-label="Risk">
          {RISKS.map((r) => (
            <button key={r} role="radio" aria-checked={risk === r} className={`pick${(state?.risk ?? 'medium') === r ? ' on' : ''}`} onClick={() => setRisk(r)} disabled={dropping || !!anim}>
              {RISK_NAMES[r]}
            </button>
          ))}
        </div>
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['risk', 'watch']} onAct={() => setBurst(null)} />
    </>
  );
}
